import React from 'react';
import { Container, Typography, Box, Paper, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/')} sx={{ color: 'text.primary' }}>
            <ArrowBackIcon />
          </IconButton>
        </Box>

        <Paper elevation={3} sx={{ p: 4, backgroundColor: 'background.paper' }}>
          <Typography variant="h3" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Privacy Policy for Prelude
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
            Last Updated: March 5, 2026
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {/* Introduction */}
          <Typography variant="h5" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
            Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            Prelude ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and web service.
          </Typography>

          {/* Information We Collect */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'bold' }}>
            Information We Collect
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Personal Information
          </Typography>
          <Typography variant="body1" paragraph>
            When you register for Prelude, we collect:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1"><strong>Name</strong> (first and last name)</Typography></li>
            <li><Typography variant="body1"><strong>Email address</strong></Typography></li>
            <li><Typography variant="body1"><strong>Password</strong> (encrypted)</Typography></li>
            <li><Typography variant="body1"><strong>Phone number</strong> (optional)</Typography></li>
            <li><Typography variant="body1"><strong>Date of birth</strong> (optional)</Typography></li>
            <li><Typography variant="body1"><strong>Profile photo</strong> (optional)</Typography></li>
            <li><Typography variant="body1"><strong>Church affiliation</strong></Typography></li>
            <li><Typography variant="body1"><strong>Role</strong> (admin, leader, or musician)</Typography></li>
            <li><Typography variant="body1"><strong>Musical instruments</strong> (for musicians)</Typography></li>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Usage Information
          </Typography>
          <Typography variant="body1" paragraph>
            We automatically collect:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1"><strong>Device information</strong> (device type, operating system, unique device identifiers)</Typography></li>
            <li><Typography variant="body1"><strong>Push notification tokens</strong> (for sending service notifications)</Typography></li>
            <li><Typography variant="body1"><strong>App usage data</strong> (features used, session duration)</Typography></li>
            <li><Typography variant="body1"><strong>Log data</strong> (IP address, access times, app crashes)</Typography></li>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Content You Create
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1"><strong>Service schedules</strong> you create or are assigned to</Typography></li>
            <li><Typography variant="body1"><strong>Songs</strong> you add to your library</Typography></li>
            <li><Typography variant="body1"><strong>Availability dates</strong> you mark</Typography></li>
            <li><Typography variant="body1"><strong>Notes and comments</strong> on services or songs</Typography></li>
          </Box>

          {/* How We Use Your Information */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'bold' }}>
            How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use the collected information to:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1"><strong>Provide and maintain</strong> the Prelude service</Typography></li>
            <li><Typography variant="body1"><strong>Authenticate</strong> your account and manage access</Typography></li>
            <li><Typography variant="body1"><strong>Send notifications</strong> about service assignments and schedule changes</Typography></li>
            <li><Typography variant="body1"><strong>Coordinate</strong> worship team schedules and availability</Typography></li>
            <li><Typography variant="body1"><strong>Improve</strong> our app features and user experience</Typography></li>
            <li><Typography variant="body1"><strong>Communicate</strong> with you about updates, support, and administrative messages</Typography></li>
          </Box>

          {/* Data Sharing and Disclosure */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'bold' }}>
            Data Sharing and Disclosure
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Within Your Church
          </Typography>
          <Typography variant="body1" paragraph>
            Your profile information (name, role, instruments, availability) is visible to other members of your church organization within Prelude.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Third-Party Service Providers
          </Typography>
          <Typography variant="body1" paragraph>
            We share data with trusted third parties who help us operate our service:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1"><strong>SendGrid</strong> - Transactional email delivery</Typography></li>
            <li><Typography variant="body1"><strong>MongoDB Atlas</strong> - Database hosting</Typography></li>
            <li><Typography variant="body1"><strong>Expo/EAS</strong> - Mobile app infrastructure</Typography></li>
            <li><Typography variant="body1"><strong>Vercel</strong> - Web hosting</Typography></li>
            <li><Typography variant="body1"><strong>Render</strong> - API server hosting</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            These providers are contractually obligated to protect your data and use it only for providing services to us.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Legal Requirements
          </Typography>
          <Typography variant="body1" paragraph>
            We may disclose your information if required by law, court order, or governmental authority, or to protect our rights, property, or safety.
          </Typography>

          {/* Data Security */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'bold' }}>
            Data Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement industry-standard security measures to protect your information:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1"><strong>Encryption</strong> of passwords using bcrypt</Typography></li>
            <li><Typography variant="body1"><strong>HTTPS/TLS</strong> for all data transmission</Typography></li>
            <li><Typography variant="body1"><strong>Secure authentication</strong> using JWT tokens</Typography></li>
            <li><Typography variant="body1"><strong>Regular security updates</strong> and monitoring</Typography></li>
            <li><Typography variant="body1"><strong>Limited access</strong> to personal data by our team</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
          </Typography>

          {/* Your Rights and Choices */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'bold' }}>
            Your Rights and Choices
          </Typography>
          <Typography variant="body1" paragraph>
            You have the right to:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1"><strong>Access</strong> your personal data</Typography></li>
            <li><Typography variant="body1"><strong>Update</strong> or correct your information in the app settings</Typography></li>
            <li><Typography variant="body1"><strong>Delete</strong> your account and associated data</Typography></li>
            <li><Typography variant="body1"><strong>Opt-out</strong> of push notifications (in device settings)</Typography></li>
            <li><Typography variant="body1"><strong>Export</strong> your data (contact us for assistance)</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            To exercise these rights, contact us at support@joinprelude.co or use the in-app settings.
          </Typography>

          {/* Data Retention */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'bold' }}>
            Data Retention
          </Typography>
          <Typography variant="body1" paragraph>
            We retain your personal information for as long as your account is active or as needed to provide services. If you delete your account:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1">Personal data is deleted within 30 days</Typography></li>
            <li><Typography variant="body1">Anonymized usage data may be retained for analytics</Typography></li>
            <li><Typography variant="body1">Church-created content (services, songs) may be retained if shared with other users</Typography></li>
          </Box>

          {/* Children's Privacy */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'bold' }}>
            Children's Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            Prelude is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us immediately.
          </Typography>

          {/* International Data Transfers */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'bold' }}>
            International Data Transfers
          </Typography>
          <Typography variant="body1" paragraph>
            Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
          </Typography>

          {/* Changes to This Privacy Policy */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'bold' }}>
            Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. We will notify you of significant changes by:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1">Posting the new policy in the app</Typography></li>
            <li><Typography variant="body1">Sending an email notification</Typography></li>
            <li><Typography variant="body1">Updating the "Last Updated" date</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            Continued use of Prelude after changes constitutes acceptance of the updated policy.
          </Typography>

          {/* Contact Us */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'bold' }}>
            Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions or concerns about this Privacy Policy, please contact us:
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body1"><strong>Email:</strong> support@joinprelude.co</Typography>
            <Typography variant="body1"><strong>Website:</strong> https://joinprelude.co</Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body2" color="text.secondary" align="center">
            © 2026 Prelude. All rights reserved.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
